import {
  createPhoto,
  linkPhotosToWorkTask,
  findWorkTasks,
  WorkTask as WorkTaskDto,
} from 'api-client';
import { retryUntilCondition } from '@planitgeo/treeplotter-domain/lib/infrastructure/retry/retry-utils';
import { removePhotoFromCache } from 'common/infrastructure/photo-management/upload.cache';
import { recordPhotoUploadOutcome } from 'common/infrastructure/work-task/workSubmissionOutbox';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import {
  MutationOptions,
  defaultMutationOptions,
} from 'common/infrastructure/remoteState/mutations.interface';
import { queryClient as globalQueryClient } from 'common/infrastructure/remoteState/persistQueryClient';

export interface UploadPhotoToWorkTaskVars {
  photoId: string;
  base64Data: string;
  workTaskId: string;
  tp2WorkRecordId?: number;
  fileName: string;
  tenantId: string;
}

interface UnlinkPhotosFromWorkTaskVars {
  tenantId: string;
  workTaskId: string;
  data: { photos: string[] };
}

const mergeUpdatedWorkTaskIntoCachedLists = (updatedWorkTask: WorkTaskDto) => {
  globalQueryClient.setQueriesData(
    { queryKey: ['findWorkTasks'] },
    (existingData: WorkTaskDto[] | undefined) => {
      if (!existingData || existingData.length === 0) {
        return existingData;
      }

      let didUpdate = false;
      const nextData = existingData.map((task) => {
        if (task.id !== updatedWorkTask.id) {
          return task;
        }
        didUpdate = true;
        return updatedWorkTask;
      });

      return didUpdate ? nextData : existingData;
    }
  );
};

const fetchWorkTaskById = async (
  tenantId: string,
  workTaskId: string
): Promise<WorkTaskDto | null> => {
  const workTasks = await findWorkTasks(
    { workTasks: [{ id: workTaskId }] },
    { tenantId },
    undefined
  );
  return workTasks?.find((task) => task.id === workTaskId) ?? null;
};

const WorkTaskPhotoRepository = {
  getUploadPhotoToWorkTaskMutationConfig(): MutationOptions {
    return {
      mutationKey: ['uploadPhotoToWorkTask'],
      ...defaultMutationOptions,
      retry: 3,
      mutationFn: async ({
        photoId,
        base64Data,
        workTaskId,
        tp2WorkRecordId,
        fileName,
        tenantId,
      }: UploadPhotoToWorkTaskVars) => {
        await createPhoto(tenantId, photoId, { filename: fileName, data: base64Data, tenantId });
        if (tp2WorkRecordId) {
          await linkPhotosToWorkTask(tenantId, workTaskId, { photos: [photoId] });
        }
      },
      onMutate: () => undefined,
      onSuccess: async (
        _: unknown,
        { photoId, workTaskId, fileName, tenantId, tp2WorkRecordId }: UploadPhotoToWorkTaskVars
      ) => {
        // The photo's bytes are now on the server (createPhoto succeeded). Record the
        // outcome durably by photoId so the runner can link it — this fires even when the
        // photo finished uploading BEFORE its submission was enqueued (the common case).
        recordPhotoUploadOutcome(photoId, 'uploaded');
        await removePhotoFromCache(workTaskId, photoId);

        if (tp2WorkRecordId) {
          // Poll until the read-side has processed the link event, then invalidate.
          await retryUntilCondition(
            async () => {
              const updatedWorkTask = await fetchWorkTaskById(tenantId, workTaskId);
              return updatedWorkTask ? [updatedWorkTask] : [];
            },
            (
              workTasks: Array<{ id: string; photos?: Array<{ id: string; fileName: string }> }>
            ) => {
              const workTask = workTasks.find((task) => task.id === workTaskId);
              if (!workTask) return false;
              return (
                workTask.photos?.some(
                  (photo) => photo.id === photoId || photo.fileName === fileName
                ) ?? false
              );
            },
            {
              maxRetries: 7,
              retryDelay: 100,
              backoffMultiplier: 2,
              logContext: {
                workTaskId,
                photoName: fileName,
                photoId,
                action: 'photo_upload_verification',
              },
            }
          );
        }

        const updatedWorkTask = await fetchWorkTaskById(tenantId, workTaskId);
        if (updatedWorkTask) {
          mergeUpdatedWorkTaskIntoCachedLists(updatedWorkTask);
        }

        await globalQueryClient.invalidateQueries({ queryKey: ['findWorkTasks'] });
        await globalQueryClient.invalidateQueries({ queryKey: ['findWorkTasksByAssetIds'] });
      },
      onError: (err: unknown, { photoId }: UploadPhotoToWorkTaskVars) => {
        loggerService.log(LoggerLevel.ERROR, 'Photo upload to work task failed', err);
        // Retries exhausted: record the failure so the runner links the rest and stops
        // waiting on this one (it's excluded, not blocking).
        recordPhotoUploadOutcome(photoId, 'failed');
      },
      onSettled: () => undefined,
    };
  },

  getUnlinkPhotosFromWorkTaskMutationCallbacks() {
    return {
      onError: (error: unknown, variables: unknown) => {
        loggerService.log(LoggerLevel.ERROR, 'Unlink photos from work task error', {
          error: error instanceof Error ? error.message : String(error),
          ...(variables as Record<string, unknown>),
        });
      },
      onSuccess: async (_data: unknown, variables: UnlinkPhotosFromWorkTaskVars) => {
        await Promise.all(
          variables.data.photos.map((photoId) =>
            removePhotoFromCache(variables.workTaskId, photoId).catch(() => {})
          )
        );

        const removedPhotoIds = new Set(variables.data.photos);
        await retryUntilCondition(
          async () => {
            const updatedWorkTask = await fetchWorkTaskById(
              variables.tenantId,
              variables.workTaskId
            );
            return updatedWorkTask ? [updatedWorkTask] : [];
          },
          (workTasks: Array<{ id: string; photos?: Array<{ id: string }> }>) => {
            const workTask = workTasks.find((task) => task.id === variables.workTaskId);
            if (!workTask) {
              return false;
            }
            return !(workTask.photos?.some((photo) => removedPhotoIds.has(photo.id)) ?? false);
          },
          {
            maxRetries: 7,
            retryDelay: 100,
            backoffMultiplier: 2,
            logContext: {
              workTaskId: variables.workTaskId,
              photoIds: variables.data.photos,
              action: 'photo_unlink_verification',
            },
          }
        );

        const updatedWorkTask = await fetchWorkTaskById(variables.tenantId, variables.workTaskId);
        if (updatedWorkTask) {
          mergeUpdatedWorkTaskIntoCachedLists(updatedWorkTask);
        }

        globalQueryClient.invalidateQueries({ queryKey: ['findWorkTasks'] });
        globalQueryClient.invalidateQueries({ queryKey: ['findWorkTasksByAssetIds'] });
      },
    };
  },
};

export default WorkTaskPhotoRepository;
